import { usePlatformFeatureFlags } from '@components/utils/DashboardFeatureFlags/usePlatformFeatureFlags'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import Yup from '@fourthwall/utils/dist/yup'

import { useAdminModeContext } from '../../../../../App/providers/AdminModeProvider'
import { getFulfillmentType } from '../../../Product.utils'
import type { ProductFormState } from '../../SingleProduct.types'
import { HS_CODE_LENGTH } from './consts'
import { getShippingViewModel } from './viewModel/getShippingViewModel'

const ShipmentSchema = Yup.object().shape({
  type: Yup.string(),
  days: Yup.object().when('type', {
    is: (type: any) => type === 'DAYS_RANGE',
    then: Yup.object().shape({
      daysMin: Yup.number().required().integer().required().min(0).max(356),
      daysMax: Yup.number()
        .required()
        .integer()
        .min(0)
        .max(356)
        .moreThan(Yup.ref('daysMin'), 'To must be greater than From.'),
    }),
  }),
  dates: Yup.object().when('type', {
    is: (type: any) => type === 'DATE_RANGE',
    then: Yup.object().shape({
      from: Yup.date().required(),
      to: Yup.date().required(),
    }),
  }),
})

const getCustomsInformationSchema = (required: boolean) =>
  Yup.lazy(() =>
    Yup.object().shape({
      shortHsCode: Yup.string()
        .nullable()
        .when([], {
          is: () => required,
          then: (schema) => schema.required('HS code is required.'),
          otherwise: (schema) => schema,
        })
        .matches(
          new RegExp(`^\\d{${HS_CODE_LENGTH}}$`),
          `HS code must be exactly ${HS_CODE_LENGTH} digits.`,
        )
        .test('digits-only', 'HS code must contain only digits.', (value) => {
          if (!value) return true

          return /^\d+$/.test(value)
        })
        .test(
          'hs-code-valid',
          'Incorrect HS code. Select an HS code from the list of suggestions.',
          function (value) {
            if (!value) return true

            return this.parent.hasHsCodes
          },
        ),
      countryOfOrigin: Yup.string()
        .nullable()
        .when([], {
          is: () => required,
          then: (schema) => schema.required('Country product was manufactured in is required.'),
          otherwise: (schema) => schema,
        }),
    }),
  )

export const useShippingValidator = () => {
  const { hasFeatureFlag } = usePlatformFeatureFlags()
  const { hasPermission } = useDashboardPermissions()
  const adminMode = useAdminModeContext()
  const featureEnabled =
    (hasFeatureFlag('assisted_product_shipping') &&
      hasPermission('products.thirdPartyLogistics.edit')) ||
    Boolean(adminMode?.isActive)

  return (values: ProductFormState) => {
    const shippingViewModel = getShippingViewModel({ values, featureEnabled })
    const fulfillmentType = getFulfillmentType(values.fulfillmentService)
    const isCustomsInformationRequired = ['CART_JAZZ', 'SHIP_MONK'].includes(fulfillmentType)

    const customsInformationSchema = getCustomsInformationSchema(isCustomsInformationRequired)

    if (shippingViewModel.view === 'SHIPPING_3PL') {
      return {
        customsInformation: customsInformationSchema,
        shippingPackageSize: Yup.string().nullable().required('Shipping package size is required'),
      }
    }

    if (
      shippingViewModel.view === 'WEIGHT_BASED_SELF_SHIPPING' ||
      shippingViewModel.view === 'FLAT_SELF_SHIPPING' ||
      shippingViewModel.view === 'SHIPPING_3PL_LEGACY' ||
      shippingViewModel.view === 'BESPOKE_SHIPPING'
    ) {
      return {
        customsInformation: customsInformationSchema,
        shipment: ShipmentSchema,
      }
    }

    return {}
  }
}
